"use strict";
/**
 * The `Operational_Incident` record — a durable, actionable record of a partially-completed
 * multi-step material write.
 *
 * ## What this is NOT
 *
 * It is **not a content-moderation record**, and the distinction is the whole reason the type exists
 * separately. A moderation record says *"a human wrote something that may violate policy"*. This
 * record says *"the platform's own data is inconsistent and needs manual repair"*. They share exactly
 * one thing — the queue a moderator reads — and nothing else. See
 * `packages/lambda-utilities/src/services/operational-incident.ts` for why
 * `createModerationRecord` is a SIBLING of the writer of this record rather than its implementation.
 *
 * ## Shape
 *
 * ```
 * PK      incident#{entityType}#{entityId}
 * SK      incident#{failed_at}#{incidentId}
 * GSI3PK  'moderation_queue'                              <- the queue moderators already read
 * GSI3SK  'unresolved#operational_failure#{failed_at}'
 *         NO TTL  <- deliberate; see below
 * ```
 *
 * ## THE ABSENCE OF `ttl` IS A LOAD-BEARING PROPERTY OF THIS TYPE
 *
 * `ttl` is deliberately **not declared here, and must never be added** (Req 8.4). Every neighbouring
 * moderation record sets one — `createModerationRecord` uses `MODERATION_RECORD_TTL_SECONDS`, which is
 * 10 years as of BL-395 and was 90 days before it
 * (`packages/lambda-utilities/src/utils/moderation-record.ts`). The reason given here used to be that
 * "a resolved policy decision stops being interesting"; BL-395 established that premise was false —
 * the neighbouring record is not resolved at creation either, and its creation-time clock was deleting
 * unresolved queue items. That does not change the conclusion for THIS type, only its justification:
 * an unresolved data inconsistency does not stop being interesting;
 * it stops being *visible*, which is worse than never having recorded it, because the ledger that
 * says which rows still need writing would expire along with the record. Adding a `ttl` here would
 * silently convert "we know exactly what to repair" into "the counters are wrong and nobody knows
 * why". `__tests__/operational-incident.test.ts` asserts the attribute's ABSENCE, and the field is
 * absent from this interface so that setting it does not typecheck.
 *
 * ## It appears in the MODERATOR UI the moment it is written
 *
 * `GSI3PK: 'moderation_queue'` is not a private partition — it is the live unified queue
 * (`services/lambda-secure/src/routes/moderation-unified-queue.ts:33-38` queries
 * `GSI3PK = 'moderation_queue' AND begins_with(GSI3SK, 'unresolved#')`). Three consequences shape the
 * required fields below, and each is met deliberately rather than by accident:
 *
 * - `content_type`, `content_id`, `status` and `created_at` are REQUIRED because
 *   `UnifiedModerationItem` declares them required and the dashboard reads them. In particular the
 *   handler's queue breakdown is keyed on `item.content_type || 'unknown'`
 *   (`moderation-unified-queue.ts:47-50`), so an incident without one would create a sidebar bucket
 *   literally named "unknown".
 * - `content_type` is `'operational_failure'`, a value no dashboard branch handles yet. The row
 *   therefore renders as "Unknown Item" with a read-only fallback panel. That gap is tracked as
 *   **BL-149** and closed by tasks 5.4/5.5 BEFORE any writer is wired up in task 7 — nothing writes
 *   this record yet, so the gap is not currently reachable.
 * - There is **no resolve route for this content type**, by design. Every existing dequeue is a
 *   content-type-specific `REMOVE GSI3PK, GSI3SK` (e.g. `routes/field-suggestion-resolve.ts:126`,
 *   `routes/resolve-pdf-moderation.ts:326`) driven by an approve/reject decision. An incident has no
 *   approve/reject meaning: it is discharged by REPAIRING DATA, replaying `recovery.remaining_work`.
 *   Dequeueing therefore belongs to the operator repair tool, not to a moderator's verdict.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6 (spec `material-usage-authority`).
 */
// Created: 2026-08-26
// Updated: 2026-08-26
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPERATIONAL_INCIDENT_QUEUE_SK_PREFIX = exports.OPERATIONAL_INCIDENT_CONTENT_TYPE = void 0;
/**
 * The `content_type` discriminator an Operational_Incident carries in the unified moderation queue.
 *
 * A value, not a type, so it is written from one place and compared against one place. It is also a
 * member of `UnifiedModerationItem['content_type']` (`../api/moderation.ts`) — declared there so the
 * queue's item type does not lie about what really appears in the partition.
 */
exports.OPERATIONAL_INCIDENT_CONTENT_TYPE = 'operational_failure';
/** The `GSI3SK` prefix, and therefore the `?type=` filter value the queue handler accepts. */
exports.OPERATIONAL_INCIDENT_QUEUE_SK_PREFIX = `unresolved#${exports.OPERATIONAL_INCIDENT_CONTENT_TYPE}#`;
